import { fmtInt, fmtMw } from "../lib/format";
import type { ColorBy } from "./MapCanvas";

type Props = {
  inView: { sites: number; units: number; mw: number; mwh: number | null };
  total: { sites: number; units: number; mw: number; mwh: number | null } | undefined;
  loading: boolean;
  onAddAll: () => void;
  colorBy: ColorBy;
  onColorBy: (c: ColorBy) => void;
};

const COLOR_OPTIONS: Array<{ value: ColorBy; label: string }> = [
  { value: "tier", label: "Tier" },
  { value: "frame", label: "Frame class" },
  { value: "segment", label: "Segment" },
  { value: "cooling", label: "Inlet cooling" },
];

export function ResultsStrip({ inView, total, loading, onAddAll, colorBy, onColorBy }: Props) {
  return (
    <div
      className="pointer-events-auto flex items-center gap-4 rounded border px-3 py-1.5"
      style={{
        borderColor: "var(--line-2)",
        background: "color-mix(in srgb, var(--panel) 94%, transparent)",
        boxShadow: "var(--shadow)",
      }}
      data-testid="results-strip"
    >
      <Stat
        label="Sites in view"
        value={fmtInt(inView.sites)}
        sub={total ? `of ${fmtInt(total.sites)}` : undefined}
        testId="stat-sites"
      />
      <Stat
        label="Units"
        value={fmtInt(inView.units)}
        sub={total ? `of ${fmtInt(total.units)}` : undefined}
        testId="stat-units"
      />
      <Stat
        label="Qualifying MW"
        value={fmtMw(inView.mw)}
        sub={total ? `of ${fmtMw(total.mw)}` : undefined}
      />
      <Stat
        label="Est. MWh/yr"
        value={inView.mwh === null ? "—" : fmtInt(inView.mwh)}
        sub={inView.mwh === null ? "no estimate yet" : undefined}
      />
      <span className="mx-1 h-6 w-px" style={{ background: "var(--line)" }} />
      <label className="flex items-center gap-1 text-[11px]" style={{ color: "var(--muted)" }}>
        Colour
        <select
          className="input"
          value={colorBy}
          onChange={(e) => onColorBy(e.target.value as ColorBy)}
          aria-label="Colour points by"
        >
          {COLOR_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        className="btn btn-sm ml-auto"
        onClick={onAddAll}
        disabled={loading || inView.sites === 0}
        data-testid="add-all-in-view"
      >
        Add all in view to list
      </button>
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  testId,
}: {
  label: string;
  value: string;
  sub?: string;
  testId?: string;
}) {
  return (
    <div className="flex flex-col leading-tight">
      <span className="eyebrow">{label}</span>
      <span className="num text-[14px]" data-testid={testId}>
        {value}{" "}
        {sub ? (
          <span className="text-[11px]" style={{ color: "var(--muted-2)" }}>
            {sub}
          </span>
        ) : null}
      </span>
    </div>
  );
}
